import { useCallback, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { isObject } from 'lodash'

import { getQuotasMap, QuotasMap } from '../../Components/shared/Quotas/utils'
import { isQuotaInSchools } from '../../Views/institution/Quotas/SchoolQuotasView/utils'
import { mapByDepartment, reduceByDepartment, reduceBySector } from '../../Components/shared/Internship/utils'
import { selectUser } from '../../reducers/UserReducer'
import { Cases } from '../../utils/constants'
import { getIdOrNull, isNonEmptyArray } from '../../utils'
import { getAllInternships } from '../../utils/api/internship'
import { getBySectors } from '../../utils/api/sectionGroup'
import { getAllQuotas, SerializationContexts as QuotaContexts } from '../../utils/api/quota'
import { getAllStudentPathways, SerializationContexts as PathwaysContexts } from '../../utils/api/studentPathway'
import { Quota } from '../../utils/entities/quota'
import { StudentPathway } from '../../utils/entities/studentPathway'
import { API_DATE } from '../../utils/momentjs'
import { resolvePromises } from '../../utils/apiHelper'
import { Internship } from '../../utils/entities/internship'

export const Modes = Object.freeze({
  SECTORS: 'sectors',
  HOSPITAL_DEPARTMENTS: 'hospitalDepartments'
})

const { LABELS } = QuotaContexts
const { SECTOR, SECTION } = PathwaysContexts

export const usePlacesMaps = ({ internshipsParams, mode, overlaySettings, schools, selectedEntities }) => {
  const user = useSelector(selectUser)

  const [maps, setMaps] = useState({
    rawQuotas: {},
    studentPathways: {},
    sectionGroups: {},
    internships: {}
  })

  const reduceBy = useMemo(() => mode === Modes.SECTORS ? reduceBySector : reduceByDepartment, [mode])

  const setInternships = useCallback(internships => setMaps(map => ({ ...map, internships })), [])

  const quotas = useMemo(() => {
    let map = {}

    const rawMap = isObject(maps.rawQuotas) ? Object.keys(maps.rawQuotas).reduce((acc, sectorId) => {
      acc[sectorId] = getQuotasMap(maps.rawQuotas[sectorId])

      return acc
    }, {}) : {}

    if (mode === Modes.SECTORS) {
      map = { ...rawMap }

      selectedEntities.forEach(sector => {
        if (sector.id in map) {
          if (overlaySettings.filterQuotasBySchools) {
            map[sector.id].places = map[sector.id].places.filter(isQuotaInSchools(schools))
          }
        } else {
          map[sector.id] = new QuotasMap()
        }
      })
    } else if (mode === Modes.HOSPITAL_DEPARTMENTS) {
      map = mapByDepartment(rawMap, selectedEntities)
    }

    return map
  }, [maps.rawQuotas, mode, overlaySettings, selectedEntities, schools])

  const loadData = useCallback((entities, period, shouldInitialize = true, sections = []) => {
    if (isNonEmptyArray(entities)) {
      const sectors = mode === Modes.SECTORS ? entities : entities.flatMap(department => department.sectors)
      const sectorIds = sectors.map(getIdOrNull)
      const toReduceBy = mode === Modes.SECTORS ? sectorIds : entities

      period = period.getDates(API_DATE)
      const { startDate, endDate } = period

      const { sectionIds } = sections.reduce((acc, sty) => {
        const sectionId = getIdOrNull(sty.section)

        if (sectionId && !acc.sectionsMap[sectionId]) {
          acc.sectionIds.push(sectionId)
          acc.sectionsMap[sectionId] = true
        }

        return acc
      }, { sectionIds: [], sectionsMap: {} })

      const promises = []

      let newRawQuotas = {}
      let newStudentPathways = {}
      let newSectionGroups = {}
      let newInternships = {}

      promises.push(getAllQuotas(user, { contexts: [LABELS], startDate, endDate, sectors: sectorIds, sections: sectionIds, 'global-places': true }).then(({ data }) => {
        const quotas = data.map(q => new Quota(q))

        newRawQuotas = reduceBySector(quotas, sectors)
      }))

      if (shouldInitialize) {
        promises.push(getAllStudentPathways(user, { contexts: [SECTION, SECTOR], sectors: sectorIds }).then(({ data }) => {
          const pathways = data.map(p => new StudentPathway(p))

          newStudentPathways = reduceBy(pathways, toReduceBy)
        }))

        promises.push(getBySectors(user, { sectors: sectorIds, contexts: [LABELS] }).then(({ data }) => {
          newSectionGroups = mode === Modes.SECTORS ? data : mapByDepartment(data, entities)
        }))
      }

      const params = internshipsParams.clone({
        filters: internshipsParams.filters.clone({
          sectors: sectorIds,
          period,
          sections: sectionIds
        })
      })

      promises.push(getAllInternships(user, params.toCase(Cases.KEBAB)).then(({ data }) => {
        const internships = data.map(i => new Internship(i))

        newInternships = reduceBy(internships, toReduceBy)
      }))

      resolvePromises(promises).then(() => {
        setMaps(maps => ({
          rawQuotas: { ...maps.rawQuotas, ...newRawQuotas },
          studentPathways: { ...maps.studentPathways, ...newStudentPathways },
          sectionGroups: { ...maps.sectionGroups, ...newSectionGroups },
          internships: { ...maps.internships, ...newInternships }
        }))
      })
    }
  }, [mode, internshipsParams, user, reduceBy])

  return useMemo(() => ({ ...maps, quotas, loadData, setInternships }), [maps, quotas, loadData, setInternships])
}
